import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { VFTone, type ToneColor } from './Badge';

/**
 * VFTimelineEvent — card événement dans le planning du jour (Tasks).
 */
export type EventStatus = 'confirmed' | 'pending' | 'completed' | 'no_show' | 'declined';
export type EventType = 'meeting' | 'task' | 'call' | 'email' | 'followup';

interface StatusMeta {
  label: string;
  tone: ToneColor;
}

function getStatusMap(t: (key: string) => string): Record<EventStatus, StatusMeta> {
  return {
    confirmed:  { label: t('common:timelineEvent.status.confirmed'),  tone: 'green' },
    pending:    { label: t('common:timelineEvent.status.pending'),    tone: 'yellow' },
    completed:  { label: t('common:timelineEvent.status.completed'), tone: 'blue' },
    no_show:    { label: t('common:timelineEvent.status.noShow'),    tone: 'red' },
    declined:   { label: t('common:timelineEvent.status.declined'),  tone: 'red' },
  };
}

const TYPE_BORDER: Record<EventType, string> = {
  meeting:  'var(--vf-green)',
  task:     'var(--vf-accent)',
  call:     'var(--vf-yellow)',
  email:    'var(--vf-blue)',
  followup: 'var(--vf-orange)',
};

interface TimelineAction {
  label: ReactNode;
  onClick?: () => void;
  variant?: string;
}

interface VFTimelineEventProps {
  time?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  tag?: ReactNode;
  status?: EventStatus;
  type?: EventType;
  actions?: TimelineAction[];
  onClick?: () => void;
  className?: string;
}

export function VFTimelineEvent({
  time,
  title,
  subtitle,
  tag,
  status,
  type = 'meeting',
  actions = [],
  onClick,
  className = '',
}: VFTimelineEventProps) {
  const { t } = useTranslation(['common']);
  const STATUS_MAP = getStatusMap(t);
  const st = status ? STATUS_MAP[status] : undefined;
  return (
    <div className={`flex gap-3 ${className}`}>
      {time && (
        <div className="w-14 shrink-0 pt-3">
          <span className="font-vf-mono text-[13px] font-medium text-[var(--vf-text-3)] tabular-nums">
            {time}
          </span>
        </div>
      )}
      <div
        onClick={onClick}
        className={[
          'flex-1 rounded-vf-lg border border-[var(--vf-border)] bg-[var(--vf-surface-2)] p-3',
          onClick ? 'cursor-pointer hover:bg-[var(--vf-surface-3)]' : '',
          'border-l-4',
        ].join(' ')}
        style={{ borderLeftColor: TYPE_BORDER[type] || TYPE_BORDER.meeting }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {title && (
                <span className="truncate text-[13px] font-semibold text-[var(--vf-text-1)]">
                  {title}
                </span>
              )}
              {subtitle && (
                <span className="truncate text-[12px] text-[var(--vf-text-3)]">
                  {subtitle}
                </span>
              )}
            </div>
            {tag && (
              <div className="mt-1 text-[11px] text-[var(--vf-text-3)]">{tag}</div>
            )}
          </div>
          {st && (
            <VFTone tone={st.tone} className="shrink-0">{st.label}</VFTone>
          )}
          {actions.length > 0 && (
            <div className="flex shrink-0 gap-1">
              {actions.map((a, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); a.onClick?.(); }}
                  className="rounded-vf-sm px-2 h-7 text-[11px] font-medium text-[var(--vf-text-2)] hover:bg-[var(--vf-surface-3)] hover:text-[var(--vf-text-1)]"
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VFTimelineEvent;
